import React, { useMemo } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import styled from "styled-components"
import * as variable from "../variables"

const PropertyListStyle = styled.div`
  padding: 80px 0;
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);

  .list-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .list-header {
    text-align: center;
    margin-bottom: 60px;

    h2 {
      font-size: 42px;
      font-weight: 700;
      margin: 0 0 16px 0;
      color: #1a1a2e;
      letter-spacing: -0.5px;
    }

    p {
      font-size: 18px;
      color: #6b7280;
      margin: 0 auto;
      max-width: 600px;
      line-height: 1.6;
    }
  }

  .neighborhood-group {
    margin-bottom: 70px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .neighborhood-header {
    display: flex;
    align-items: center;
    margin-bottom: 35px;
    gap: 20px;
  }

  .neighborhood-title {
    font-size: 26px;
    font-weight: 600;
    color: ${variable.blue};
    margin: 0;
    white-space: nowrap;
  }

  .neighborhood-line {
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, ${variable.blue} 0%, transparent 100%);
    border-radius: 1px;
  }

  .properties-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;

    @media (max-width: ${variable.tabletWidth}) {
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
    }

    @media (max-width: ${variable.mobileWidth}) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  .property-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    text-decoration: none;
    color: inherit;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

      .property-image img {
        transform: scale(1.05);
      }

      .view-link {
        color: ${variable.blue};
        gap: 10px;
      }
    }
  }

  .property-image {
    width: 100%;
    height: 220px;
    background-color: #e5e7eb;
    overflow: hidden;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
    }

    .placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
      color: #9ca3af;
      font-size: 14px;
    }
  }

  .property-content {
    padding: 24px;
    display: flex;
    flex-direction: column;
    flex: 1;

    h3 {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 12px 0;
      color: #1a1a2e;
      line-height: 1.3;
    }

    .description {
      font-size: 15px;
      color: #6b7280;
      line-height: 1.6;
      margin: 0 0 20px 0;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      flex: 1;
    }

    .view-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: ${variable.red};
      font-weight: 600;
      font-size: 15px;
      text-decoration: none;
      transition: all 0.2s ease;
      margin-top: auto;

      svg {
        width: 18px;
        height: 18px;
        transition: transform 0.2s ease;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }
  }

  .no-properties {
    text-align: center;
    padding: 60px 40px;
    color: #6b7280;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .view-all-container {
    text-align: center;
    margin-top: 60px;
  }

  .view-all-button {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 16px 32px;
    background: linear-gradient(135deg, ${variable.blue}, #022a7a);
    color: white;
    font-size: 16px;
    font-weight: 600;
    text-decoration: none;
    border-radius: 50px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 14px rgba(3, 56, 160, 0.3);

    svg {
      width: 20px;
      height: 20px;
      transition: transform 0.2s ease;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(3, 56, 160, 0.4);

      svg {
        transform: translateX(4px);
      }
    }
  }
`

const PropertyList = ({ section }) => {
  const data = useStaticQuery(graphql`
    query PropertyListQuery {
      allSanityProperty {
        nodes {
          headline
          tagline
          address
          promoted
          slug {
            current
          }
          main_image {
            asset {
              url
            }
          }
          neighborhood {
            headline
            slug {
              current
            }
          }
          _rawDescription
        }
      }
      allSanityNeighborhood {
        nodes {
          headline
          slug {
            current
          }
        }
      }
    }
  `)

  const {
    headline,
    subheadline,
    groupByNeighborhood = true,
    promotedOnly = false,
  } = section || {}

  const allProperties = data.allSanityProperty.nodes
  const neighborhoods = data.allSanityNeighborhood.nodes

  // Filter properties based on promoted toggle
  const properties = useMemo(() => {
    if (promotedOnly) {
      return allProperties.filter(p => p.promoted === true)
    }
    return allProperties
  }, [allProperties, promotedOnly])

  // Group properties by neighborhood
  const groupedProperties = useMemo(() => {
    if (!groupByNeighborhood) {
      return [{ neighborhood: null, properties }]
    }

    const groups = []
    const neighborhoodMap = new Map()

    // Create groups for each neighborhood
    neighborhoods.forEach(n => {
      neighborhoodMap.set(n.slug?.current, {
        neighborhood: n,
        properties: [],
      })
    })

    // Add "Other" group for properties without neighborhood
    neighborhoodMap.set(null, {
      neighborhood: null,
      properties: [],
    })

    // Assign properties to groups
    properties.forEach(property => {
      const neighborhoodSlug = property.neighborhood?.slug?.current || null
      const group = neighborhoodMap.get(neighborhoodSlug) || neighborhoodMap.get(null)
      group.properties.push(property)
    })

    // Convert to array and filter out empty groups
    neighborhoodMap.forEach(group => {
      if (group.properties.length > 0) {
        groups.push(group)
      }
    })

    return groups
  }, [properties, neighborhoods, groupByNeighborhood])

  // Extract plain text from portable text description
  const getDescriptionText = (rawDescription) => {
    if (!rawDescription || !Array.isArray(rawDescription)) return ""

    return rawDescription
      .filter(block => block._type === "block")
      .map(block => {
        if (block.children) {
          return block.children
            .filter(child => child._type === "span")
            .map(span => span.text)
            .join("")
        }
        return ""
      })
      .join(" ")
      .substring(0, 200)
  }

  return (
    <PropertyListStyle>
      <section
        className="list-container"
        aria-label="Property listings"
        itemScope
        itemType="https://schema.org/ItemList"
      >
        {(headline || subheadline) && (
          <header className="list-header">
            {headline && <h2 itemProp="name">{headline}</h2>}
            {subheadline && <p itemProp="description">{subheadline}</p>}
          </header>
        )}

        <meta itemProp="numberOfItems" content={String(properties.length)} />

        {properties.length === 0 ? (
          <div className="no-properties" role="status">No properties available.</div>
        ) : (
          <>
            {groupedProperties.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="neighborhood-group"
                role="region"
                aria-label={group.neighborhood?.headline || "Properties"}
              >
                {groupByNeighborhood && group.neighborhood && (
                  <header className="neighborhood-header">
                    <h3 className="neighborhood-title">
                      {group.neighborhood.headline}
                    </h3>
                    <div className="neighborhood-line" aria-hidden="true" />
                  </header>
                )}

                <ul className="properties-grid" style={{ listStyle: "none", padding: 0 }}>
                  {group.properties.map((property, propertyIndex) => (
                    <li
                      key={property.slug?.current}
                      itemScope
                      itemType="https://schema.org/Accommodation"
                      itemProp="itemListElement"
                    >
                      <meta itemProp="position" content={String(propertyIndex + 1)} />
                      <Link
                        to={`/property/${property.slug?.current}`}
                        className="property-card"
                        itemProp="url"
                        aria-label={`View ${property.headline} - Corporate housing property`}
                      >
                        <div className="property-image">
                          {property.main_image?.asset?.url ? (
                            <img
                              src={`${property.main_image.asset.url}?w=400&h=220&fit=crop`}
                              alt={`${property.headline} - Furnished corporate apartment in Kansas City`}
                              itemProp="image"
                              loading="lazy"
                            />
                          ) : (
                            <div className="placeholder" aria-hidden="true">No image</div>
                          )}
                        </div>
                        <article className="property-content">
                          <h3 itemProp="name">{property.headline}</h3>
                          {property._rawDescription && (
                            <p className="description" itemProp="description">
                              {getDescriptionText(property._rawDescription)}
                            </p>
                          )}
                          <span className="view-link" aria-hidden="true">
                            View Property
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </span>
                        </article>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {promotedOnly && (
              <nav className="view-all-container" aria-label="View all properties">
                <Link
                  to="/locations"
                  className="view-all-button"
                  aria-label="View more corporate housing properties in Kansas City"
                >
                  View More Properties
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </nav>
            )}
          </>
        )}
      </section>
    </PropertyListStyle>
  )
}

export default PropertyList
